<template>
  <div>
    <canvas
     :width="canvasWidth"
     :height="canvasHeight"
     ref="canvas"
     @dragover.prevent
     @drop="onDrop"
     @mousedown="onDragStart"
     @mouseup="onDragEnd"
     @mousemove="onMouseMove"
     @click="clicked"
     v-bind:class="cursor"
     ></canvas>

     <!-- render photo-cropper -->
     <div ref="canvasGrid" class="photo-cropper">
       <div class="photo-cropper__thirds ">
         <div class="photo-cropper__thirds-line photo-cropper__thirds-line--top-horizontal"></div>
         <div class="photo-cropper__thirds-line photo-cropper__thirds-line--bottom-horizontal"></div>
         <div class="photo-cropper__thirds-line photo-cropper__thirds-line--left-vertical"></div>
         <div class="photo-cropper__thirds-line photo-cropper__thirds-line--right-vertical"></div>
       </div>
     </div>
  </div>
</template>

<script>
export default {
  name: 'avatar',
  props: {
    image: {
      type: String,
      default: ''
    },
    borderX: {
      type: Number,
      default: 235
    },
    borderY: {
      type: Number,
      default: 0
    },
    borderRadius: {
      type: Number,
      default: 0
    },
    width: {
      type: Number,
      default: 200
    },
    height: {
      type: Number,
      default: 200
    },
    showPlaceholder: {
      type: Boolean,
      default: true
    },
    color: {
      type: Array,
      default: function () { return [0, 0, 0, 0.5] }
    }
  },
  data () {
    return {
      cursor: '',
      scale: 1,
      canvas: null,
      context: null,
      dragged: false,
      imageLoaded: false,
      changed: false,
      state: {
        drag: false,
        my: null,
        mx: null,
        image: {
          x: 0,
          y: 0,
          resource: null
        }
      }
    }
  },
  computed: {
    canvasWidth () {
      return this.getDimensions().canvas.width
    },
    canvasHeight () {
      return this.getDimensions().canvas.height
    }
  },
  mounted () {
    this.canvas = this.$refs.canvas
    this.context = this.canvas.getContext('2d')
    this.paint()

    // load the image
    if (this.image) this.loadImage(this.image)
  },
  methods: {
    drawRoundedRect (context, x, y, width, height, borderRadius) {
      if (borderRadius === 0) {
        context.rect(x, y, width, height)
      } else {
        const widthMinusRad = width - borderRadius
        const heightMinusRad = height - borderRadius
        context.translate(x, y)
        context.arc(borderRadius, borderRadius, borderRadius, Math.PI, Math.PI * 1.5)
        context.lineTo(widthMinusRad, 0)
        context.arc(widthMinusRad, borderRadius, borderRadius, Math.PI * 1.5, Math.PI * 2)
        context.lineTo(width, heightMinusRad)
        context.arc(widthMinusRad, heightMinusRad, borderRadius, Math.PI * 2, Math.PI * 0.5)
        context.lineTo(borderRadius, height)
        context.arc(borderRadius, heightMinusRad, borderRadius, Math.PI * 0.5, Math.PI)
        context.translate(-x, -y)
      }
    },
    paint () {
      this.context.save()
      this.context.translate(0, 0)
      this.context.fillStyle = 'rgba(' + this.color.slice(0, 4).join(',') + ')'

      let borderRadius = this.borderRadius
      const dimensions = this.getDimensions()
      const borderY = dimensions.borderY
      const borderX = dimensions.borderX
      const height = dimensions.canvas.height
      const width = dimensions.canvas.width

      // clamp border radius between zero (perfect rectangle) and half the size without borders (perfect circle or "pill")
      borderRadius = Math.max(borderRadius, 0)
      borderRadius = Math.min(borderRadius, width / 2 - borderY, height / 2 - borderY)

      this.context.beginPath()
      // inner rect, possibly rounded
      this.drawRoundedRect(this.context, borderX, borderY, width - borderX * 2, height - borderY * 2, borderRadius)

      this.context.rect(width, 0, -width, height) // outer rect, drawn "counterclockwise"
      this.context.fill('evenodd')

      this.context.restore()
    },
    getDimensions () {
      return {
        width: this.width,
        height: this.height,
        borderY: this.borderY,
        borderX: this.borderX,
        canvas: {
          width: this.width + (this.borderX * 2),
          height: this.height + (this.borderY * 2)
        }
      }
    },
    onDrop (e) {
      e = e || window.event
      e.stopPropagation()
      e.preventDefault()
      if (e.dataTransfer && e.dataTransfer.files.length) {
        // this.props.onDropFile(e)
        const reader = new FileReader()
        const file = e.dataTransfer.files[0]
        this.changed = true
        reader.onload = (e) => this.loadImage(e.target.result)
        reader.readAsDataURL(file)
      }
    },
    onDragStart (e) {
      e = e || window.event
      e.preventDefault()
      this.state.drag = true

      this.state.mx = null
      this.state.my = null
      this.cursor = 'cursor-grabbing'
    },
    onDragEnd (e) {
      if (this.state.drag) {
        this.state.drag = false
        this.cursor = ''
      }
    },
    onMouseMove (e) {
      e = e || window.event
      if (!this.state.drag) return

      this.dragged = true
      this.changed = true

      let imageState = this.state.image
      const lastX = imageState.x
      const lastY = imageState.y

      const mousePositionX = e.targetTouches ? e.targetTouches[0].pageX : e.clientX
      const mousePositionY = e.targetTouches ? e.targetTouches[0].pageY : e.clientY
      const newState = { mx: mousePositionX, my: mousePositionY, image: imageState }

      if (this.state.mx && this.state.my) {
        const xDiff = (this.state.mx - mousePositionX) / this.scale
        const yDiff = (this.state.my - mousePositionY) / this.scale

        imageState.y = this.getBoundedY(lastY - yDiff, this.scale)
        imageState.x = this.getBoundedX(lastX - xDiff, this.scale)
      }
      this.state.mx = newState.mx
      this.state.my = newState.my
      this.state.image = imageState
    },
    loadImage (imageURL) {
      let imageObj = new Image()
      imageObj.onload = () => {
        this.handleImageReady(imageObj)
      }

      // imageObj.onerror = this.props.onLoadFailure
      if (!this.isDataURL(imageURL)) imageObj.crossOrigin = 'anonymous'
      imageObj.src = imageURL.replace(/^https:\/\//i, 'http://')
    },
    handleImageReady (image) {
      let imageState = this.getInitialSize(image.width, image.height)
      imageState.resource = image

      imageState.x = 0
      imageState.y = 0

      let oldState = this.state
      oldState.drag = false
      oldState.image = imageState
      this.state.image.x = 0
      this.state.image.y = 0
      this.state.image.resource = image
      this.state.image.width = imageState.width
      this.state.image.height = imageState.height
      this.state.drag = false
      this.scale = 1
      this.$emit('avatar:image-ready', this.scale)
      this.imageLoaded = true
      this.cursor = 'cursor-grab'
    },
    getInitialSize (width, height) {
      let newHeight
      let newWidth

      const dimensions = this.getDimensions()
      const canvasRatio = dimensions.height / dimensions.width
      const imageRatio = height / width

      if (canvasRatio > imageRatio) {
        newHeight = (this.getDimensions().height)
        newWidth = (width * (newHeight / height))
      } else {
        newWidth = (this.getDimensions().width)
        newHeight = (height * (newWidth / width))
      }

      return {
        height: newHeight,
        width: newWidth
      }
    },
    isDataURL (str) {
      if (str === null) return false

      /* eslint-disable no-useless-escape */
      const regex = /^\s*data:([a-z]+\/[a-z]+(;[a-z\-]+=[a-z\-]+)?)?(;base64)?,[a-z0-9!$&',()*+;=\-._~:@\/?%\s]*\s*$/i
      return !!str.match(regex)
    },
    getBoundedX (x, scale) {
      let image = this.state.image
      let dimensions = this.getDimensions()
      let widthDiff = Math.floor((image.width - dimensions.width / scale) / 2)
      widthDiff = Math.max(0, widthDiff)

      return Math.max(-widthDiff, Math.min(x, widthDiff))
    },
    getBoundedY (y, scale) {
      let image = this.state.image
      let dimensions = this.getDimensions()
      let heightDiff = Math.floor((image.height - dimensions.height / scale) / 2)
      heightDiff = Math.max(0, heightDiff)
      return Math.max(-heightDiff, Math.min(y, heightDiff))
    },
    paintImage (context, image, borderX, borderY) {
      if (image.resource) {
        let position = this.calculatePosition(image, borderX, borderY)
        context.save()
        context.globalCompositeOperation = 'destination-over'
        context.drawImage(image.resource, position.x, position.y, position.width, position.height)
        context.restore()
      }
    },
    calculatePosition (image, borderX, borderY) {
      let newImage = image || this.state.image

      let dimensions = this.getDimensions()
      let width = newImage.width * this.scale
      let height = newImage.height * this.scale

      let widthDiff = (width - dimensions.width) / 2
      let heightDiff = (height - dimensions.height) / 2
      let x = newImage.x * this.scale - widthDiff + borderX
      let y = newImage.y * this.scale - heightDiff + borderY

      return {
        x,
        y,
        height,
        width
      }
    },
    changeScale (sc) {
      this.changed = true
      this.scale = sc
    },
    redraw () {
      this.context.clearRect(0, 0, this.getDimensions().canvas.width, this.getDimensions().canvas.height)
      this.paint()
      this.paintImage(this.context, this.state.image, this.borderX, this.borderY)
    },
    getImage () {
      const cropRect = this.getCroppingRect()
      const image = this.state.image

      // get actual pixel coordinates
      cropRect.x *= image.resource.width
      cropRect.y *= image.resource.height
      cropRect.width *= image.resource.width
      cropRect.height *= image.resource.height

      // create a canvas with the correct dimensions
      const canvas = document.createElement('canvas')
      canvas.width = cropRect.width
      canvas.height = cropRect.height

      // draw the full-size image at the correct position,
      // the image gets truncated to the size of the canvas.
      canvas.getContext('2d').drawImage(image.resource, -cropRect.x, -cropRect.y)

      return canvas
    },
    getImageScaled () {
      const { width, height } = this.getDimensions()
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      // don't paint a border here, as it is the resulting image
      this.paintImage(canvas.getContext('2d'), this.state.image, 0, 0)

      return canvas
    },
    imageChanged () {
      return this.changed
    },
    getCroppingRect () {
      const dim = this.getDimensions()
      const frameRect = { x: dim.borderX, y: dim.borderY, width: dim.width, height: dim.height }
      const imageRect = this.calculatePosition(this.state.image, dim.border)
      return {
        x: (frameRect.x - imageRect.x) / imageRect.width,
        y: (frameRect.y - imageRect.y) / imageRect.height,
        width: frameRect.width / imageRect.width,
        height: frameRect.height / imageRect.height
      }
    },
    clicked (e) {
      if (this.dragged) this.dragged = false
    },
    fileSelected (e) {
      let files = e.target.files || e.dataTransfer.files
      if (!files.length) return

      let reader = new FileReader()
      this.changed = true
      reader.onload = (e) => {
        this.loadImage(e.target.result)
      }
      reader.readAsDataURL(files[0])
    }
  },
  watch: {
    state: {
      handler (val, oldval) {
        if (this.imageLoaded) { this.redraw() }
      },
      deep: true
    },
    scale () {
      if (this.imageLoaded) { this.redraw() }
    }
  }
}
</script>
